import React, { useState, useEffect, useMemo } from 'react';
import { Form as BootstrapForm, FilterGrid, FilterField } from '@/components/ui';
import { Form, FormFilterOptions } from '../../../services/formService';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import { StandoutDropdown } from '../../../components/StandoutDropdown';
import FiltersCard from '../../../components/FiltersCard';
import { HIRED_EQUIPMENT_FORM_TYPE } from '../../hired-equipment/config/moduleCopy';
import type { ApprovalFormType } from '../config/approvalFormTypes';

/**
 * Advanced (card) filters for the Submissions page.
 * Every dropdown filter is multi-select, so each value is a list.
 * Dates stay single values.
 */
export interface FormAdvancedFilters {
  employeeNames: string[];
  subcontractorNames: string[];
  startDate: string | null;
  endDate: string | null;
  foremanAreaIds: string[];
  serviceAreaIds: string[];
}

/** Stable list comparison (order-sensitive, which is enough: we only ever append/remove). */
const sameList = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

interface FormFiltersProps {
  forms: Form[];
  filterOptions: FormFilterOptions;
  filters: FormAdvancedFilters;
  formType?: ApprovalFormType;
  onClearFilters: () => void;
  onApplyFilters: (filters: FormAdvancedFilters) => void;
}

const EMPTY_FILTERS: FormAdvancedFilters = {
  employeeNames: [],
  subcontractorNames: [],
  startDate: null,
  endDate: null,
  foremanAreaIds: [],
  serviceAreaIds: []
};

const FormFilters: React.FC<FormFiltersProps> = ({
  forms,
  filterOptions,
  filters,
  formType = 'all',
  onClearFilters,
  onApplyFilters
}) => {
  // Local state for pending filters (not yet applied)
  const [pendingFilters, setPendingFilters] = useState<FormAdvancedFilters>(filters);

  // Update pending filters when applied filters change (e.g., after clear)
  useEffect(() => {
    setPendingFilters(filters);
  }, [filters]);

  // Use server-provided filter options for service areas, foreman areas, employee names, and subcontractor names
  const serviceAreas = filterOptions.serviceAreas || [];
  const foremanAreas = filterOptions.foremanAreas;
  const employeeNames = filterOptions.employeeNames || [];
  const subcontractorNames = filterOptions.subcontractorNames || [];

  // Build employee to foreman area mapping from current forms (for cross-filtering)
  const employeeToForemanMap = useMemo(() => {
    const map = new Map<string, Set<number>>();

    forms.forEach(form => {
      const employeeNameField = form.fields.find(
        field => field.field === 'EmployeeName' ||
                field.question.toLowerCase().includes('employee name')
      );

      const employeeName = employeeNameField?.value || '';

      if (employeeName && form.homeForemanAreaId) {
        if (!map.has(employeeName)) {
          map.set(employeeName, new Set());
        }
        map.get(employeeName)?.add(form.homeForemanAreaId);
      }
    });

    return map;
  }, [forms]);

  // Build foreman area to employee mapping from current forms (for cross-filtering)
  const foremanToEmployeeMap = useMemo(() => {
    const map = new Map<number, Set<string>>();

    forms.forEach(form => {
      const employeeNameField = form.fields.find(
        field => field.field === 'EmployeeName' ||
                field.question.toLowerCase().includes('employee name')
      );

      const employeeName = employeeNameField?.value || '';

      if (employeeName && form.homeForemanAreaId) {
        if (!map.has(form.homeForemanAreaId)) {
          map.set(form.homeForemanAreaId, new Set());
        }
        map.get(form.homeForemanAreaId)?.add(employeeName);
      }
    });

    return map;
  }, [forms]);

  const pendingServiceAreaKey = pendingFilters.serviceAreaIds.join(',');
  const pendingForemanAreaKey = pendingFilters.foremanAreaIds.join(',');
  const pendingEmployeeKey = pendingFilters.employeeNames.join('|');

  // Filter foreman areas based on the SELECTED SET of service areas (multiselect).
  // No service areas selected => every foreman area is offered.
  const filteredForemanAreas = useMemo(() => {
    let areas = foremanAreas;

    // Filter by service areas if any are selected
    const selectedServiceAreaIds = new Set(
      pendingFilters.serviceAreaIds
        .map(id => parseInt(id, 10))
        .filter(id => !isNaN(id))
    );
    if (selectedServiceAreaIds.size > 0) {
      areas = areas.filter(area => selectedServiceAreaIds.has(area.serviceAreaId));
    }

    // Filter by employees if any are selected and we have current forms data:
    // keep foreman areas that any of the selected employees submitted from.
    if (pendingFilters.employeeNames.length > 0 && forms.length) {
      const allowedForemanAreaIds = new Set<number>();
      pendingFilters.employeeNames.forEach(name => {
        employeeToForemanMap.get(name)?.forEach(id => allowedForemanAreaIds.add(id));
      });
      if (allowedForemanAreaIds.size > 0) {
        areas = areas.filter(area => allowedForemanAreaIds.has(area.id));
      }
    }

    return [...areas].sort((a, b) => a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foremanAreas, pendingServiceAreaKey, pendingEmployeeKey, employeeToForemanMap, forms.length]);

  // Filter employees based on the selected foreman areas (only if we have current forms data)
  const filteredEmployeeNames = useMemo(() => {
    const sortedAll = [...employeeNames].sort((a, b) => a.localeCompare(b));
    if (pendingFilters.foremanAreaIds.length === 0 || !forms.length) {
      return sortedAll;
    }

    const allowedEmployeeNames = new Set<string>();
    pendingFilters.foremanAreaIds.forEach(rawId => {
      const areaId = parseInt(rawId, 10);
      if (isNaN(areaId)) return;
      foremanToEmployeeMap.get(areaId)?.forEach(name => allowedEmployeeNames.add(name));
    });

    // No mapping data for the selected areas => don't hide everything
    if (allowedEmployeeNames.size === 0) {
      return sortedAll;
    }

    return sortedAll.filter(name => allowedEmployeeNames.has(name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeNames, pendingForemanAreaKey, foremanToEmployeeMap, forms.length]);

  // Handle local filter changes (don't apply immediately)
  const handlePendingListChange = (
    name: 'employeeNames' | 'subcontractorNames' | 'foremanAreaIds' | 'serviceAreaIds',
    value: (string | number)[]
  ) => {
    const nextValues = value.map(String);
    setPendingFilters(prev => {
      const newFilters: FormAdvancedFilters = { ...prev, [name]: nextValues };

      // Service area is multiselect now: instead of clearing the foreman area outright,
      // drop only the foreman areas that no longer belong to a selected service area.
      if (name === 'serviceAreaIds' && !sameList(nextValues, prev.serviceAreaIds)) {
        const selectedServiceAreaIds = new Set(
          nextValues.map(id => parseInt(id, 10)).filter(id => !isNaN(id))
        );
        if (selectedServiceAreaIds.size === 0) {
          // "All service areas": keep whatever foreman areas were picked
          newFilters.foremanAreaIds = prev.foremanAreaIds;
        } else {
          newFilters.foremanAreaIds = prev.foremanAreaIds.filter(rawId => {
            const areaId = parseInt(rawId, 10);
            const area = foremanAreas.find(fa => fa.id === areaId);
            return area ? selectedServiceAreaIds.has(area.serviceAreaId) : false;
          });
        }
      }

      return newFilters;
    });
  };

  const handlePendingDateChange = (name: 'startDate' | 'endDate', value: string) => {
    setPendingFilters(prev => ({ ...prev, [name]: value }));
  };

  const hasUnappliedChanges = () => {
    return !sameList(pendingFilters.employeeNames, filters.employeeNames) ||
           !sameList(pendingFilters.subcontractorNames, filters.subcontractorNames) ||
           pendingFilters.startDate !== filters.startDate ||
           pendingFilters.endDate !== filters.endDate ||
           !sameList(pendingFilters.foremanAreaIds, filters.foremanAreaIds) ||
           !sameList(pendingFilters.serviceAreaIds, filters.serviceAreaIds);
  };

  const hasActiveFilters = () => {
    return filters.employeeNames.length > 0 ||
           filters.subcontractorNames.length > 0 ||
           filters.startDate !== null ||
           filters.endDate !== null ||
           filters.foremanAreaIds.length > 0 ||
           filters.serviceAreaIds.length > 0;
  };

  const handleApplyFilters = () => {
    onApplyFilters(pendingFilters);
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setPendingFilters({ ...EMPTY_FILTERS });
    onClearFilters();
  };

  return (
    <FiltersCard
      title="Form Filters"
      subtitle="Filter forms by service area, foreman area, employee, and date range"
      collapsible={true}
      defaultCollapsed={false}
      showApplyButton={true}
      onApply={handleApplyFilters}
      applyDisabled={!hasUnappliedChanges()}
      showClearButton={true}
      onClear={handleClearFilters}
      hasActiveFilters={hasActiveFilters()}
    >
      <BootstrapForm>
        <FilterGrid>
          <FilterField>
            <StandoutDropdown<FormFilterOptions['serviceAreas'][0]>
              type="multiselect"
              options={serviceAreas}
              value={pendingFilters.serviceAreaIds}
              onChange={(value) => handlePendingListChange('serviceAreaIds', value)}
              getValue={(area) => area.id.toString()}
              formatOption={(area) => area.name}
              placeholder="All Service Areas"
              className="tw-w-full"
              label="Service Area"
              noOptionsMessage="No service areas available"
            />
          </FilterField>

          <FilterField>
            <StandoutDropdown<FormFilterOptions['foremanAreas'][0]>
              type="multiselect"
              options={filteredForemanAreas}
              value={pendingFilters.foremanAreaIds}
              onChange={(value) => handlePendingListChange('foremanAreaIds', value)}
              getValue={(area) => area.id.toString()}
              formatOption={(area) => area.name}
              placeholder="All Foreman Areas"
              className="tw-w-full"
              label="Foreman Area"
              noOptionsMessage="No foreman areas available for selected filters"
            />
          </FilterField>

          {/* Show Employee Name dropdown for Daily Log or all form types */}
          {(formType === 'Daily Log' || formType === 'all') && (
            <FilterField>
              <MultiSelectDropdown<string>
                options={filteredEmployeeNames}
                value={pendingFilters.employeeNames}
                onChange={(value) => handlePendingListChange('employeeNames', value)}
                getValue={(name) => name}
                formatOption={(name) => name}
                placeholder="All Employees"
                className="tw-w-full"
                label="Employee Name"
                noOptionsMessage="No employees available for selected filters"
              />
            </FilterField>
          )}

          {/* Show Subcontractor Name dropdown for Hired Equipment or all form types */}
          {(formType === HIRED_EQUIPMENT_FORM_TYPE || formType === 'all') && (
            <FilterField>
              <MultiSelectDropdown<string>
                options={subcontractorNames}
                value={pendingFilters.subcontractorNames}
                onChange={(value) => handlePendingListChange('subcontractorNames', value)}
                getValue={(name) => name}
                formatOption={(name) => name}
                placeholder="All Subcontractors"
                className="tw-w-full"
                label="Subcontractor / Company"
                noOptionsMessage="No subcontractors available"
              />
            </FilterField>
          )}

          {/* Start and end are one range, so they occupy one (double-width) grid item and
              cannot be split across a wrap. Previously they were two independent twelfths that
              could land on different rows at different label heights — the reported
              "work date filters are misaligned". */}
          <FilterField span={2}>
            <div className="tw-grid tw-grid-cols-2 tw-items-end tw-gap-2">
              <BootstrapForm.Group controlId="approvals-work-start-date">
                <BootstrapForm.Label>Work Start Date</BootstrapForm.Label>
                <BootstrapForm.Control
                  type="date"
                  size="sm"
                  name="startDate"
                  value={pendingFilters.startDate || ''}
                  onChange={(e) => handlePendingDateChange('startDate', e.target.value)}
                  title="Filter submissions whose work date (formDate or any ActualStartTime / line date) falls in this range"
                />
              </BootstrapForm.Group>
              <BootstrapForm.Group controlId="approvals-work-end-date">
                <BootstrapForm.Label>Work End Date</BootstrapForm.Label>
                <BootstrapForm.Control
                  type="date"
                  size="sm"
                  name="endDate"
                  value={pendingFilters.endDate || ''}
                  onChange={(e) => handlePendingDateChange('endDate', e.target.value)}
                  title="Filter submissions whose work date (formDate or any ActualStartTime / line date) falls in this range"
                />
              </BootstrapForm.Group>
            </div>
          </FilterField>
        </FilterGrid>
      </BootstrapForm>
    </FiltersCard>
  );
};

export default FormFilters;
